import type { ModelAssetFile, ModelAssetNode } from '../../project-model/assets/model-asset-types'
import type { AssemblyDocument } from '../../project-model/assembly-document/assembly-document-types'
import { resolveUniqueAssemblyOfflinePackageFile } from '../../project-model/loading/assembly-offline-package-file'
import { migrateLegacyPuppyProjectModelDocument } from './migrateLegacyPuppyProjectModelDocument'
import { parsePuppyPackageProjectModelDocuments } from './parsePuppyPackage'
import type { PuppyProjectModelDocument } from './types'

/** 当前 `.puppy` 的 project-model 根直系 coverage 上下文。 */
export interface PuppyAssemblyProjectModelCoverageContext {
  /** 当前装配节点实际加载的仓库包路径。 */
  readonly packageFileId: string
  /** project-model 已解析并物化的根直系 assemblyNodeId。 */
  readonly rootDirectAssemblyNodeIds: ReadonlySet<string>
}

/**
 * 解析当前 ModelAssetNode 的唯一 assembly `.puppy` 离线包。
 *
 * @param node 当前加载单元 root。
 * @returns 唯一装配离线包；非装配或无离线包时返回 undefined。
 */
export function resolvePuppyAssemblyPackageRootFile(node: ModelAssetNode): ModelAssetFile | undefined {
  if (node.nodeType !== 'assembly') {
    return undefined
  }

  return resolveUniqueAssemblyOfflinePackageFile({
    // 完整 GLB 根包虽然沿用 `.step.puppy` 容器，但其内部没有标准 PuppyCAD
    // assembly/project-model 文档，必须在最底层离线包解析边界显式隔离。
    files: node.packageFiles.filter((file) => file.loadMode !== 'complete-glb-scene'),
    repositoryId: node.repositoryId,
    commitId: node.commitId,
  })
}

/**
 * 判断模型节点是否为 assembly `.puppy` 加载单元。
 *
 * @param node 当前模型节点。
 * @returns 存在唯一装配离线包时返回 true。
 */
export function isPuppyAssemblyPackageRoot(node: ModelAssetNode): boolean {
  return Boolean(resolvePuppyAssemblyPackageRootFile(node))
}

/**
 * 从最终 project-model 创建根直系 coverage 上下文。
 *
 * @param input 当前装配 root 与原生或迁移后的 project-model。
 * @returns 精确 package/node_where coverage；无包或无 project-model 时返回 null。
 */
export function createPuppyAssemblyProjectModelCoverageContext(input: {
  readonly root: ModelAssetNode
  readonly projectModel: PuppyProjectModelDocument | null
}): PuppyAssemblyProjectModelCoverageContext | null {
  const packageFile = resolvePuppyAssemblyPackageRootFile(input.root)
  if (!packageFile || !input.projectModel) {
    return null
  }
  const projectModel = input.projectModel

  return {
    packageFileId: packageFile.fileId,
    rootDirectAssemblyNodeIds: new Set(
      projectModel.occurrences
        .filter(occurrence => occurrence.parentOccurrenceId === projectModel.rootOccurrenceId)
        .map(occurrence => occurrence.assemblyNodeId),
    ),
  }
}

/**
 * 从当前实际 `.puppy` 与同节点装配文档解析显式 project-model coverage 上下文。
 *
 * 原生 project-model 优先；v4 包沿 SDK 既有迁移链生成 project-model。迁移后的根直系允许为空，
 * 只有确实无法迁移时才返回 null，禁止从运行树反推未知 coverage。
 *
 * @param input 当前装配 root、包字节与对应外部 `_assembly.json`。
 * @returns 原生或迁移 project-model 的显式 coverage 上下文。
 */
export async function parsePuppyAssemblyProjectModelCoverageContext(input: {
  readonly root: ModelAssetNode
  readonly bytes: Uint8Array
  readonly assemblyDocument: AssemblyDocument
}): Promise<PuppyAssemblyProjectModelCoverageContext | null> {
  if (!resolvePuppyAssemblyPackageRootFile(input.root)) {
    return null
  }

  const documents = await parsePuppyPackageProjectModelDocuments(input.bytes)
  const projectModel = documents.projectModel ?? migrateLegacyPuppyProjectModelDocument({
    assembly: documents.assembly,
    assemblyDocument: input.assemblyDocument,
  })

  return createPuppyAssemblyProjectModelCoverageContext({
    root: input.root,
    projectModel,
  })
}
